/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import { cssMap } from '@atlaskit/css';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	container: {
		paddingTop: token('space.400'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
	},
	paragraph: {
		paddingBottom: token('space.300'),
	},
});

/**
 * Text-line widths that simulate the look of a wall-of-text / chat-style
 * response being loaded.  Two "paragraph" groups are rendered with a gap
 * between them so the shimmer feels natural.
 */
const PARAGRAPH_1_WIDTHS = ['100%', '95%', '100%', '85%', '90%', '60%'] as const;
const PARAGRAPH_2_WIDTHS = ['100%', '90%', '95%', '70%', '100%', '85%', '45%'] as const;

const LINE_HEIGHT = '14px';

export interface InsightsFeedLoadingShimmerProps {
	/** Test ID prefix for targeting in tests */
	testId?: string;
}

/**
 * InsightsFeedLoadingShimmer — A skeleton loading placeholder that mimics
 * a wall-of-text / paragraph-style chat response.
 *
 * Renders multiple lines of shimmering skeleton bars at varying widths,
 * grouped into two visual "paragraphs" to simulate the shape of the actual
 * insights content that will load in the InsightDetail view.
 *
 * Displayed while insights are being fetched from the backend.
 */
export const InsightsFeedLoadingShimmer = ({
	testId = 'insights-feed-loading-shimmer',
}: InsightsFeedLoadingShimmerProps): JSX.Element => (
	<Box xcss={styles.container} testId={testId}>
		{/* First paragraph block */}
		<Stack xcss={styles.paragraph} space="space.100">
			{PARAGRAPH_1_WIDTHS.map((width, i) => (
				<Skeleton
					key={`p1-${i}`}
					width={width}
					height={LINE_HEIGHT}
					borderRadius={token('radius.small')}
					isShimmering
				/>
			))}
		</Stack>

		{/* Second paragraph block */}
		<Stack space="space.100">
			{PARAGRAPH_2_WIDTHS.map((width, i) => (
				<Skeleton
					key={`p2-${i}`}
					width={width}
					height={LINE_HEIGHT}
					borderRadius={token('radius.small')}
					isShimmering
				/>
			))}
		</Stack>
	</Box>
);
